import { ArrowRight, Check, Copy, RotateCw, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../../api/envelope';
import type {
  Campaign,
  CampaignNextStep,
  CampaignRecipientFilter,
  CampaignResendSegment,
  CampaignSummaryPayload,
  CampaignSummarySource,
} from '../../api/types';
import { useTimeFormat } from '../../lib/tenantTime';
import { useLoad } from '../../lib/useLoad';
import { useComms } from './context';

/**
 * Where each point's figures live on Results, named as the link reads. A
 * baseline point compares against OTHER campaigns, so nothing on this screen
 * is its source and it gets no link.
 */
const SOURCE_LABELS: Record<CampaignSummarySource, string | null> = {
  funnel: 'What it did',
  timing: 'Hour by hour',
  links: 'Links',
  replies: 'Replies',
  bounces: 'Didn’t reach',
  bookings: 'Reservations',
  baseline: null,
};

/** How often a summary another tab is writing is looked for, and for how long. */
const BUSY_RETRY_MS = 4000;
const BUSY_RETRIES = 6;

/**
 * The AI summary a finished campaign's Results open with — a headline, what
 * happened, and what to do next.
 *
 * Written ONCE, on the first view of a finished campaign by somebody who may
 * use AI, and stored: unlike the copy review (CampaignReviewPanel), which is
 * asked for because it would otherwise bill every pass through the builder,
 * this costs one call per campaign, and a summary that waits for somebody to
 * think of pressing a button is a summary nobody reads. The one other write it
 * makes on its own is when the bookings window closes on a summary written
 * before it did — once per visit, so a failing model cannot be asked on every
 * render.
 *
 * The model writes the sentences and none of the numbers: the server checks
 * every figure against the facts it gave it (services/campaignSummary.js).
 * The next steps are not AI at all — they are worked out from the campaign's
 * own counts on every read, which is why they still show when no summary can
 * be written.
 */
export function CampaignSummaryCard({
  campaign,
  mayFollowUp,
  onFollowUp,
  onRecipients,
  onSource,
}: {
  campaign: Campaign;
  /** Whether this person may create a follow-up draft (campaigns: draft). */
  mayFollowUp: boolean;
  onFollowUp: (segment: CampaignResendSegment) => void;
  onRecipients: (filter: CampaignRecipientFilter) => void;
  onSource: (source: CampaignSummarySource) => void;
}) {
  const { source, capabilities } = useComms();
  const { listTime, fmtDate } = useTimeFormat();
  // Permissive while /meta is in flight, like every capability read in the module.
  const aiLevel = capabilities?.access.features.ai;
  const mayUseAi = aiLevel !== 'off';

  const load = useLoad(
    () => source.api.getCampaignSummary(campaign.id),
    [source, campaign.id, campaign.status],
  );
  const payload = load.data;
  const [writing, setWriting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Null until asked; false means the company has no AI to write with.
  const [aiReady, setAiReady] = useState<boolean | null>(null);
  const [copied, setCopied] = useState(false);
  /**
   * What this visit has already written on its own, so an automatic write is
   * made at most once each — a write that fails must not be retried by the
   * next render.
   */
  const autoWritten = useRef<{ first: boolean; final: boolean }>({ first: false, final: false });
  const unmounted = useRef(false);
  useEffect(() => {
    // Set here as well as cleared below: StrictMode mounts, unmounts and
    // mounts again, and a flag only ever set to true would drop every answer.
    unmounted.current = false;
    return () => {
      unmounted.current = true;
    };
  }, []);

  const write = (force: boolean, attempt = 0) => {
    setWriting(true);
    setError(null);
    source.api.writeCampaignSummary(campaign.id, force).then(
      (next: CampaignSummaryPayload) => {
        if (unmounted.current) return;
        setWriting(false);
        load.setData(next);
      },
      (e: unknown) => {
        if (unmounted.current) return;
        // Somebody else is writing it right now (another agent opened it at the
        // same moment). Theirs will be stored, so wait for it rather than pay
        // for a second one.
        if (e instanceof ApiError && e.kind === 'conflict' && attempt < BUSY_RETRIES) {
          setTimeout(() => {
            if (unmounted.current) return;
            source.api.getCampaignSummary(campaign.id).then(
              (next) => {
                if (unmounted.current) return;
                if (next.summary) {
                  setWriting(false);
                  load.setData(next);
                } else {
                  write(force, attempt + 1);
                }
              },
              () => write(force, attempt + 1),
            );
          }, BUSY_RETRY_MS);
          return;
        }
        setWriting(false);
        setError(e instanceof ApiError ? e.message : 'The summary could not be written — try again.');
      },
    );
  };

  const finished = payload?.finished ?? false;
  const summary = payload?.summary ?? null;
  const countedUntil = payload?.bookingsCountedUntil ?? null;

  // Ask whether there is a model to write with only when about to need one.
  const wantsFirst = finished && !summary && mayUseAi;
  useEffect(() => {
    if (!wantsFirst || aiReady !== null) return;
    let cancelled = false;
    source.api.getAiAvailability().then(
      (answer) => {
        if (!cancelled) setAiReady(answer.available);
      },
      () => {
        if (!cancelled) setAiReady(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [source, wantsFirst, aiReady]);

  useEffect(() => {
    if (!finished || !mayUseAi || writing) return;
    if (!summary && aiReady === true && !autoWritten.current.first) {
      autoWritten.current.first = true;
      write(false);
    } else if (
      summary &&
      !summary.bookingsFinal &&
      countedUntil !== null &&
      Date.parse(countedUntil) <= Date.now() &&
      !autoWritten.current.final
    ) {
      // Written while bookings were still coming in; the window has closed
      // since, so the figures it quotes have stopped moving and can be final.
      autoWritten.current.final = true;
      write(true);
    }
    // `write` is re-created every render and each trigger above fires it once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished, mayUseAi, summary, aiReady, countedUntil, writing]);

  // Quietly absent on a failed read: the figures below this card are the
  // record, and an error box at the top of Results would read as the campaign
  // having gone wrong.
  if (load.error || !payload || !payload.finished) return null;
  const steps = payload.nextSteps;
  if (!summary && !writing && !error && steps.length === 0 && (aiReady === false || !mayUseAi)) return null;

  const copy = () => {
    const lines = [
      `${campaign.name}`,
      ...(summary ? [summary.headline, '', ...summary.points.map((point) => `• ${point.text}`)] : []),
      ...(steps.length ? ['', 'What to do next:', ...steps.map((step) => `• ${step.text}`)] : []),
    ];
    navigator.clipboard?.writeText(lines.join('\n')).then(
      () => {
        setCopied(true);
        setTimeout(() => {
          if (!unmounted.current) setCopied(false);
        }, 2000);
      },
      () => {},
    );
  };

  const stepAction = (step: CampaignNextStep) => {
    if (step.kind === 'follow_up' && step.segment) {
      if (!mayFollowUp) return null;
      const segment = step.segment;
      return (
        <button type="button" className="cbtn cbtn-primary" onClick={() => onFollowUp(segment)}>
          Create follow-up
        </button>
      );
    }
    if (step.kind === 'recipients' && step.filter) {
      const filter = step.filter;
      return (
        <button type="button" className="cbtn" onClick={() => onRecipients(filter)}>
          {filter === 'replied' ? 'Open them' : 'See them'}
        </button>
      );
    }
    return null;
  };

  return (
    <section className="cmg-summary" data-card="summary" aria-label="AI summary">
      <div className="cmg-summary-head">
        <span className="cmg-summary-badge">
          <Sparkles size={12} />
          AI summary
        </span>
        <span className="cmg-summary-when">
          {summary
            ? `Written ${listTime(summary.writtenAt)}${
                !summary.bookingsFinal && payload.bookingsCountedUntil
                  ? ` · reservations are still being counted until ${fmtDate(payload.bookingsCountedUntil)}, and it is rewritten then`
                  : ''
              }`
            : writing
              ? 'Writing it now…'
              : ''}
        </span>
        <div style={{ flex: 1 }} />
        {summary && (
          <button type="button" className="cbtn" onClick={copy} title="Copy the summary as text">
            {copied ? <Check size={13} /> : <Copy size={13} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        )}
        {summary && mayUseAi && (
          <button
            type="button"
            className="cbtn"
            disabled={writing}
            title="Write the summary again from today's numbers"
            onClick={() => write(true)}
          >
            {writing ? <span className="btn-spin" /> : <RotateCw size={13} />}
            {writing ? 'Writing…' : 'Write it again'}
          </button>
        )}
      </div>

      {summary ? (
        <div className="cmg-summary-headline">{summary.headline}</div>
      ) : writing ? (
        <div className="cmg-summary-skeleton" aria-label="Writing the summary">
          <span style={{ width: '72%' }} />
          <span style={{ width: '54%' }} />
        </div>
      ) : error ? (
        <div className="cmg-summary-missing">
          <span>{error}</span>
          {mayUseAi && (
            <button type="button" className="cbtn" onClick={() => write(false)}>
              Try again
            </button>
          )}
        </div>
      ) : (
        <div className="cmg-summary-missing">
          {!mayUseAi
            ? 'No summary has been written yet — it is written the first time somebody with AI access opens this campaign.'
            : aiReady === false
              ? 'No summary — AI is not set up for your company yet.'
              : 'Getting ready to write a summary…'}
        </div>
      )}

      {((summary?.points.length ?? 0) > 0 || steps.length > 0) && (
        <div className="cmg-summary-cols">
          {summary && summary.points.length > 0 && (
            <div className="cmg-summary-col">
              <div className="cmg-summary-label">What happened</div>
              <ul className="cmg-summary-points">
                {summary.points.map((point) => {
                  const label = SOURCE_LABELS[point.source];
                  return (
                    <li key={point.text}>
                      <span>{point.text}</span>
                      {label && (
                        <button
                          type="button"
                          className="cmg-summary-source"
                          title={`Show where this comes from: ${label}`}
                          onClick={() => onSource(point.source)}
                        >
                          {label}
                          <ArrowRight size={11} />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {steps.length > 0 && (
            <div className="cmg-summary-col">
              <div className="cmg-summary-label">What to do next</div>
              <div className="cmg-summary-steps">
                {steps.map((step) => (
                  <div key={step.text} className="cmg-summary-step">
                    <span>{step.text}</span>
                    {stepAction(step)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {summary && (
        <div className="cmg-summary-foot">
          Written from this campaign’s own numbers and replies — every figure in it was checked
          against them. Reservations are what followed the campaign, not proof that it caused them.
        </div>
      )}
    </section>
  );
}
